import "./Admin.css";
import { useEffect, useState, useRef } from "react";
import { MainContainer, MainContent } from "../common/components/Layout";
import { callApi, handleErrorAdmin } from "../common/api/Api";
import { useErrorBoundary } from "react-error-boundary";
import { getListForAdmin, AdminData, deleteSerial, AdminSerial } from "./AdminListApi";
import { licenseNumberForView } from "../common/utils/Utils";
import Button from "@mui/material/Button";
import { DataGrid, GridColDef, GridValueGetterParams, GridRenderCellParams, GridRowId } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { gridLang, isJp } from "../i18n/LangUtil";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { format } from "date-fns";
import { DialogTitle } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdminListExportButton } from "./AdminListExportButton";
import { LoginUserComponent } from "./LoginUserComponent";

interface ConfirmDialogData {
  open: boolean;
  data?: AdminSerial;
}

const DeleteButton = styled(Button)({
  backgroundColor: "#d32f2f",
  "&:hover": {
    backgroundColor: "#c62828",
  },
  "&:active": {
    backgroundColor: "#d32f2f",
  },
});

/**
 * 管理者画面のコンポーネントです。
 *
 * @returns 管理者画面のコンポーネント
 */
export const AdminListPage = (): JSX.Element => {
  const { showBoundary } = useErrorBoundary();
  const { t, i18n } = useTranslation();
  const [adminData, setAdminData] = useState<AdminData>();
  const selectedRow = useRef<AdminSerial>();
  const [serialConfirmDialog, setSerialConfirmDialog] = useState<ConfirmDialogData>({
    open: false,
  });

  useEffect(() => {
    (async () => {
      await callApi(async () => {
        const result = await getListForAdmin();
        if (result.error) {
          return handleErrorAdmin(result.error?.response?.data, result.error, showBoundary, t);
        }
        if (!result.data || !result.data.options || !result.data.list) {
          setAdminData({} as AdminData);
          return;
        }
        setAdminData(result.data);
      });
    })();
  }, []);

  if (!adminData || !adminData.options || !adminData.list) {
    return <></>;
  }

  const openSerialDialog = (rowId: GridRowId) => {
    selectedRow.current = adminData.list.find((v) => v.id === rowId);
    setSerialConfirmDialog({ open: true, data: selectedRow.current });
  };

  const closeSerialDialog = () => {
    setSerialConfirmDialog({ open: false, data: selectedRow.current });
  };

  const deleteSerialClicked = () => {
    setSerialConfirmDialog({ open: false, data: selectedRow.current });
    (async () => {
      await callApi(async () => {
        if (!selectedRow || !selectedRow.current) {
          return;
        }
        const targetId = selectedRow.current.id;
        const result = await deleteSerial(targetId);
        if (result.error) {
          return handleErrorAdmin(result.error?.response?.data, result.error, showBoundary, t);
        }
        if (!result.data || result.data !== "OK") {
          return;
        }
        const rows = adminData.list.filter((v) => v.id !== targetId);
        setAdminData({ ...adminData, list: rows });
      });
    })();
  };

  const dateForView = (targetDate: string) => {
    if (!targetDate) {
      return "";
    }
    return format(new Date(targetDate), "yyyy/MM/dd");
  };

  const viewConfirm = (adminInfo: AdminSerial | undefined) => {
    if (!adminInfo) {
      return <></>;
    }
    const cameraData = `${t("common.serialNumberOfCameraLabel")} : ${adminInfo.serialNumber}`;

    const options = adminData.options
      .filter((option) => adminInfo.optionIdList.some((optionId) => optionId === option.id))
      .map((op) => {
        return <li>{isJp(i18n) ? op.nameJa : op.nameEn}</li>;
      });

    return (
      <div className="flex flex-col gap-y-2">
        <div>{cameraData}</div>
        <div>
          <div>{`${t("admin.registeredOptionLabel")} : `}</div>
          <ul className="p-2">{options}</ul>
        </div>
        <div>
          <div>{`${t("admin.customerInfoLabel")} : `}</div>
          <ul className="p-2">
            <li>{`${t("admin.companyName")} : ${adminInfo.tenantName}`}</li>
            <li>{`${t("common.owner")} : ${adminInfo.familyName} ${adminInfo.givenName}`}</li>
            <li>{`${t("common.emailAddress")} : ${adminInfo.loginMailAddress}`}</li>
          </ul>
        </div>
      </div>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "deleteButton",
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      width: 90,
      renderCell: (params) => {
        return (
          <Button variant="contained" onClick={() => openSerialDialog(params.id)}>
            {t("admin.removeSerialButton")}
          </Button>
        );
      },
    },
    {
      field: "tenantName",
      headerName: t("admin.companyName"),
      sortable: true,
      disableColumnMenu: true,
      width: 380,
    },
    {
      field: "name",
      headerName: t("common.owner"),
      sortable: true,
      disableColumnMenu: true,
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.familyName || t("admin.removed")} ${params.row.givenName || ""}`,
    },
    {
      field: "loginMailAddress",
      headerName: t("common.emailAddress"),
      sortable: true,
      disableColumnMenu: true,
      width: 300,
      valueGetter: (params: GridValueGetterParams) => `${params.row.loginMailAddress || t("admin.removed")}`,
    },
    {
      field: "serialNumber",
      headerName: t("common.serialNumber"),
      sortable: true,
      disableColumnMenu: true,
      width: 150,
    },
  ];

  for (const opt of adminData.options) {
    columns.push({
      field: "option" + opt.id,
      headerName: isJp(i18n) ? opt.nameJa : opt.nameEn,
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div className="text-left">
            {`${licenseNumberForView(params.row["licenseNumber" + opt.id])}`}
            <br />
            {`${dateForView(params.row["licenseAt" + opt.id])}`}
          </div>
        );
      },
    });
  }

  return (
    <MainContainer>
      <MainContent className="flex flex-col gap-y-4">
        <LoginUserComponent />
        <div className="flex flex-col items-start">
          <Button variant="text" href="/admin/menu">
            {t("admin.backMenuButton")}
          </Button>
        </div>
        <div className="flex flex-col items-end">
          <AdminListExportButton adminData={adminData} />
        </div>
        <div>
          <DataGrid
            rows={adminData.list}
            columns={columns}
            hideFooter
            localeText={gridLang(i18n).components.MuiDataGrid.defaultProps.localeText}
          />
        </div>
        <Dialog open={serialConfirmDialog.open} onClose={closeSerialDialog}>
          <DialogTitle>
            <div>{`${t("admin.confirmRemoveSerialMessage")}`}</div>
            <div>{`${t("admin.confirmCannotRecoverSerialMessage")}`}</div>
          </DialogTitle>
          <DialogContent>
            <div>
              <div className="p-6 text-base">{viewConfirm(serialConfirmDialog?.data)}</div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeSerialDialog}>
              {t("common.cancelButton")}
            </Button>
            <DeleteButton variant="contained" onClick={deleteSerialClicked}>
              {t("common.removeButton")}
            </DeleteButton>
          </DialogActions>
        </Dialog>
        <div className="flex flex-col items-start">
          <Button variant="text" href="/admin/menu">
            {t("admin.backMenuButton")}
          </Button>
        </div>
      </MainContent>
    </MainContainer>
  );
};
